import { useId, useMemo, useState } from 'react'

type Layer = {
  id: string
  title: string
  bullets: string[]
}

const LAYERS: Layer[] = [
  {
    id: 'value',
    title: 'Business Value',
    bullets: [
      'Revenue, savings, risk reduction',
      'KPIs tied to outcomes, not demos',
      'Clear owners, SLAs, and ROI',
    ],
  },
  {
    id: 'apps',
    title: 'Agents & Apps',
    bullets: [
      'Task/goal-driven agents and UIs',
      'Multi-step tools, memory, planning',
      'Human-in-the-loop and guardrails',
    ],
  },
  {
    id: 'orchestration',
    title: 'Orchestration & Tools',
    bullets: [
      'Workflows, connectors, retrieval',
      'Function calling, tool catalogs',
      'Eval loops and prompt systems',
    ],
  },
  {
    id: 'models',
    title: 'Models',
    bullets: [
      'LLMs, embeddings, rerankers',
      'Fine-tuning, adapters, distillation',
      'Routing and model selection',
    ],
  },
  {
    id: 'knowledge',
    title: 'Knowledge & Data',
    bullets: [
      'Docs, code, tickets, CRM, DBs',
      'Freshness, lineage, quality gates',
      'Security and access control',
    ],
  },
  {
    id: 'infra',
    title: 'Infrastructure',
    bullets: [
      'Compute, networking, observability',
      'Secrets, policy, compliance',
      'Cost controls and scaling',
    ],
  },
]

function trapezoidPath(
  x: number,
  y: number,
  topWidth: number,
  bottomWidth: number,
  height: number,
) {
  const leftTop = x - topWidth / 2
  const rightTop = x + topWidth / 2
  const leftBottom = x - bottomWidth / 2
  const rightBottom = x + bottomWidth / 2
  const yBottom = y + height
  return `M ${leftTop} ${y} L ${rightTop} ${y} L ${rightBottom} ${yBottom} L ${leftBottom} ${yBottom} Z`
}

export default function AIPyramid() {
  const gid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const gradId = `pyr_grad_${gid}`
  const glowId = `pyr_glow_${gid}`
  const [selected, setSelected] = useState<number>(0)

  const layers = useMemo(() => LAYERS, [])
  const layerCount = layers.length

  // Geometry
  const vb = { w: 1000, h: 520 }
  const cx = vb.w * 0.38 // center x for pyramid
  const topY = 40
  const totalH = 420
  const pad = 6
  const sliceH = (totalH - pad * (layerCount - 1)) / layerCount
  const topW = 220
  const baseW = 640
  const wStep = (baseW - topW) / (layerCount - 1)

  return (
    <section id="pyramid" className="pyr-section" aria-label="AI Value Pyramid">
      <div className="container pyr-wrap">
        <div className="pyr-head">
          <h2>AI Value Pyramid</h2>
          <p className="muted">Interactive view of the AI stack from infrastructure to business value.</p>
        </div>

        <div className="pyr-grid">
          <div className="pyr-vis" role="img" aria-label="Pyramid layers">
            <svg className="pyr-svg" viewBox={`0 0 ${vb.w} ${vb.h}`}>
              <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="var(--brand)" />
                  <stop offset="100%" stopColor="#6fb1ff" />
                </linearGradient>
                <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* background grid lines */}
              <g opacity=".14">
                {Array.from({ length: 7 }).map((_, i) => (
                  <line key={`g${i}`} x1={40} y1={topY + i * (sliceH + pad)} x2={vb.w - 40} y2={topY + i * (sliceH + pad)} stroke="var(--border)" />
                ))}
              </g>

              {/* Layers */}
              {layers.map((layer, i) => {
                const y = topY + i * (sliceH + pad)
                const tW = topW + i * wStep
                const bW = tW + wStep
                const d = trapezoidPath(cx, y, tW, bW, sliceH)
                const isActive = selected === i
                return (
                  <g key={layer.id} className={`pyr-layer ${isActive ? 'active' : ''}`}>
                    <path
                      d={d}
                      fill="var(--panel)"
                      stroke={isActive ? `url(#${gradId})` : 'var(--border)'}
                      strokeWidth={isActive ? 2 : 1}
                      onMouseEnter={() => setSelected(i)}
                      onFocus={() => setSelected(i)}
                      onClick={() => setSelected(i)}
                    />
                    <text
                      className="pyr-label"
                      x={cx}
                      y={y + sliceH / 2 + 5}
                      textAnchor="middle"
                    >
                      {layer.title}
                    </text>
                  </g>
                )
              })}

              {/* Apex glow and caption line to right panel */}
              <g>
                <circle cx={cx} cy={topY + sliceH / 2} r="34" fill="transparent" stroke={`url(#${gradId})`} filter={`url(#${glowId})`} opacity=".35" />
                <line x1={cx + 60} y1={topY + sliceH / 2} x2={vb.w - 40} y2={topY + sliceH / 2} stroke={`url(#${gradId})`} strokeDasharray="10 12" opacity=".6" />
              </g>
            </svg>
          </div>

          <div className="pyr-detail" role="region" aria-live="polite">
            <div className="pyr-detail-card">
              <div className="pyr-detail-tabs" role="tablist" aria-label="Pyramid layers">
                {layers.map((l, i) => (
                  <button
                    key={l.id}
                    role="tab"
                    aria-selected={selected === i}
                    className={`pyr-tab ${selected === i ? 'active' : ''}`}
                    onClick={() => setSelected(i)}
                  >
                    {l.title}
                  </button>
                ))}
              </div>

              <div className="pyr-detail-body">
                <h4>{layers[selected].title}</h4>
                <ul>
                  {layers[selected].bullets.map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
